/* eslint-disable spire/export-styles */
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Popover from "@ucs/MobiusOverrides/Popover/Popover";
import { MainNavigationStyles, MappedLink } from "@ucs/Overrides/Widgets/Header/MainNavigation";
import setIsLocalSelectedPLP from "@ucs/Store/Custom/SetIsLocalSelectedPLP";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}

const mapDispatchToProps = {
    setIsLocalSelectedPLP,
};

type Props = ThemeProps<BaseTheme> & OwnProps & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

const MegaMenuContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const MegaMenuItem = styled.div`
    min-width: 150px;
    margin: 0px 20px;
    > span {
        color: ${UCSColors.gray03};
        font-size: 18px;
        font-weight: 500;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 1px solid ${UCSColors.gray07};
        width: 100%;
        display: inline-block;
    }
    p {
        max-width: 150px;
        color: ${UCSColors.gray04};
        font-weight: 300;
        font-size: 16px;
    }
`;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover("");
        }
    }

    closePopoverAndSetFilterId = (obj: MappedLink, url: string) => {
        const locationFilterId = getCookie("LocationBasedAttributeValue") || "";
        if (obj.children && obj.children.length === 0 && (url.includes("catalogue") || url.includes("catalog"))) {
            this.props.setIsLocalSelectedPLP({ isLocalSelectedPLP: true });
            if (locationFilterId !== "") {
                this.props.history.push(`${url}?attributeValueIds=${locationFilterId}`);
            }
            else {
                this.props.history.push(url);
            }
        } else {
            this.props.setIsLocalSelectedPLP({ isLocalSelectedPLP: false });
            this.props.history.push(url);
        }
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    closePopover = (url: string) => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            history,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        const isProductMega = hasChildren && link.isProductMenu;
        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
                onClick={() => this.closePopover(link.url)}
            >
                {link.title}
                {/* {isCascading && downIcon} */}
            </Link>
        );
        if (isMega && link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    onClick={() => this.closePopover("/catalog/type")}
                    href="/catalog/type"
                >
                    {link.title}
                    {/* {(isCascading || isMega) && downIcon} */}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }
        if (isMega && !isProductMega) {
            menuItem = (
                <Popover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement>) => {
                        if (this.props.container.current?.offsetParent) {
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const height = link.children ? `${link.children.length * 32 + 40}px` : `${0}px`;
                            return {
                                top,
                                position: "fixed",
                                left: 0,
                                width: `100%`,
                                display: "flex",
                                justifyContent: "right",
                                height,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                    singleColumn={true}
                >
                    <StyledWrapper
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                        style={{ flexDirection: "column", width: "auto", justifyContent: "flex-start" }}
                    >
                        <StyledWrapper style={{ maxWidth: 500, margin: "0px" }}>
                            {link
                                .children!.filter(child => !child.excludeFromNavigation)
                                .map((child, index) => {
                                    return (
                                        <StyledWrapper
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                        >
                                            <Link
                                                {...styles.megaMenuLink}
                                                href={child.url}
                                                onClick={() => this.closePopover(child.url)}
                                            >
                                                {child.title}
                                            </Link>
                                            {child.children && (
                                                <ul>
                                                    {child.children
                                                        .filter(grandChild => {
                                                            if (grandChild.excludeFromNavigation) {
                                                                return false;
                                                            }
                                                            if (grandChild.type === "VmiUsersPage") {
                                                                const currentMode = getCookie(NavigationModeCookieName);
                                                                if (!currentMode || currentMode === "Storefront") {
                                                                    return false;
                                                                }
                                                            }
                                                            return true;
                                                        })
                                                        .map((grandChild, index) => (
                                                            // eslint-disable-next-line react/no-array-index-key
                                                            <li key={index}>
                                                                <Link
                                                                    className="mega-menu-link"
                                                                    {...styles.megaMenuLink}
                                                                    onClick={() => this.closePopover(grandChild.url)}
                                                                    href={grandChild.url}
                                                                >
                                                                    {grandChild.title}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                </ul>
                                            )}
                                        </StyledWrapper>
                                    );
                                })}
                        </StyledWrapper>
                    </StyledWrapper>
                </Popover>
            );
        }

        if (isProductMega) {
            const buildChildRow = (child: MappedLink) => {
                let hasGreatGrandChildren = false;

                if (child.children) {
                    child.children.forEach(grandChild => {
                        if (grandChild.children && grandChild.children.length > 0) {
                            hasGreatGrandChildren = true;
                        }
                    });

                    return (
                        <ul style={hasGreatGrandChildren ? { display: "flex", gap: "30px" } : {}}>
                            {child.children
                                .filter(grandChild => !grandChild.excludeFromNavigation)
                                .map((grandChild, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <li key={index} style={hasGreatGrandChildren ? { minWidth: "150px" } : {}}>
                                        <Link
                                            {...(grandChild.children && grandChild.children.length > 0
                                                ? styles.megaMenuLinkHeader
                                                : styles.megaMenuLink)}
                                            onClick={() => this.closePopoverAndSetFilterId(grandChild, grandChild.url)}
                                            data-test-selector="Savi-test-5"
                                            style={
                                                hasGreatGrandChildren
                                                    ? { fontSize: "18px", color: UCSColors.gray03, marginBottom: "8px" }
                                                    : {}
                                            }
                                        >
                                            {grandChild.title}
                                        </Link>
                                        {grandChild.children && (
                                            <ul>
                                                {grandChild.children
                                                    .filter(greatGrandChild => !greatGrandChild.excludeFromNavigation)
                                                    .map((greatGrandChild, index) => (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <li key={index}>
                                                            <Link
                                                                className="mega-menu-link"
                                                                {...styles.megaMenuLink}
                                                                onClick={() =>
                                                                    this.closePopoverAndSetFilterId(
                                                                        greatGrandChild,
                                                                        greatGrandChild.url,
                                                                    )
                                                                }
                                                            >
                                                                {greatGrandChild.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                        </ul>
                    );
                }
                return <ul></ul>;
            };

            menuItem = (
                <Popover
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement>) => {
                        if (this.props.container.current) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = document.querySelector("header")?.getBoundingClientRect();
                            const top = rect ? rect.top + rect.height : 0;
                            return {
                                top,
                                position: "fixed",
                                left: "0px",
                                width: "100%",
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                    singleColumn={false}
                >
                    <MegaMenuContainer
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <MegaMenuItem
                                    className="mega-menu-grid-item"
                                    width={(12 / link.numberOfColumns!) as GridWidths}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    {...styles.megaMenuGridItem}
                                >
                                    <Link
                                        className="mega-menu-heading-link"
                                        {...styles.megaMenuHeading}
                                        href={child.url}
                                        onClick={() => this.closePopover(child.url)}
                                    >
                                        {child.title}
                                    </Link>
                                    {child.children && buildChildRow(child)}
                                </MegaMenuItem>
                            ))}
                        {link.blurbTitle && link.blurbDescription && (
                            <MegaMenuItem>
                                <span>{link.blurbTitle}</span>
                                <p>{link.blurbDescription}</p>
                            </MegaMenuItem>
                        )}
                    </MegaMenuContainer>
                </Popover>
            );
        }

        return menuItem;
    }
}

export default connect(null, mapDispatchToProps)(withTheme(withHistory(MainNavigationItem)));
